import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DetailList } from '@skyline-platform/alchemy';
import { getLogger } from '@utils/general';
import { getStreams } from '@utils/graphql/creators/streams';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import { useEffectOnce } from '@utils/react/hooks';
import { CopyButton } from '@components/CopyButton/CopyButton';
import './StreamUrlsDisplay.css';
import { NotSupportedWithH265 } from '@components/InfoIcon/InfoIcon';

const log = getLogger('StreamsUrlsDisplay');

const StreamUrlEntry = (attributes) => {
  const {
    streamId,
    urlValue,
    type,
    accoutrement,
  } = attributes ?? {};
  const url = new URL(urlValue);
  const urlValueWithoutToken = url.href.replace(url.search, '');
  const urlDisplayId = [type, streamId].join('-');
  const urlValueId = [urlDisplayId, 'value'].join('-');
  return (
    <>
      <article tabIndex={-1}>
        {accoutrement} <span
          id={urlDisplayId}
          title={urlValueWithoutToken}
          className='url-display'>{urlValueWithoutToken ?? 'fetching...'}</span>
      </article>
      <span id={urlValueId} className='url-value'>{urlValue}</span>
      <CopyButton id={urlValueId} tooltip='Copy Stream URL' />
    </>
  );
};

const doUpdateFromStreamChange = (model, data) => {
  if (model.toLowerCase() !== 'stream') {
    return false;
  }
  const {
    current,
    previous,
  } = JSON.parse(data);
  return (
    current.name !== previous.name ||
    current.appliance_id !== previous.appliance_id
  );
};

const doUpdateFromApplianceChange = (model, data, applianceId) => {
  if (model.toLowerCase() !== 'appliance') {
    return false;
  }
  const {
    current,
    previous,
  } = JSON.parse(data);
  if (current.id !== applianceId) {
    return false;
  }
  return (
    current.management_address !== previous.management_address ||
    current?.public_address !== previous?.public_address
  );
};

export const StreamUrlsDisplay = (attributes) => {
  const {
    streamId,
  } = attributes ?? {};
  const storedApplianceId = useRef(null);
  const [detailListInfo, setDetailListInfo] = useState({});
  const getUrlInformation = useCallback(
    () => {
      const { query, execute } = getStreams(streamId);
      query.addReturnProperties(
        'rtmpAddress',
        'rtmpSecureAddress',
        'rtsp_address',
        'rtspSecureAddress',
        'hlsSecureAddress',
        'hlsSecurePrerollAddress',
        'hls_fmp4_secure_address',
        'clspsAddress',
        'appliance.id'
      );
      execute()
        .then(({ error, data }) => {
          if (error) {
            log.error(`An error occurred while retrieving stream data: ${error}`);
            return;
          }

          const urlInfo = {};
          storedApplianceId.current = data.appliance.id;
          Array.of(
            {
              title: 'RTMP URL (Flash)',
              property: 'rtmp_address',
              type: 'rtmp',
            },
            {
              title: 'RTMPS URL (Flash)',
              property: 'rtmp_secure_address',
              type: 'rtmps',
            }
          ).forEach(({
            title,
            property,
            type,
          }) => urlInfo[title] = (<StreamUrlEntry
            streamId={`${streamId}-${property}`}
            urlValue={data[property]}
            type={type}
            accoutrement={(<NotSupportedWithH265 id={`${streamId}-${property}`} />)}
          />));
          Array.of(
            {
              title: 'RTSP URL (Android)',
              property: 'rtsp_address',
              type: 'rtsp',
            },
            {
              title: 'RTSPS URL (Android)',
              property: 'rtsp_secure_address',
              type: 'rtsps',
            },
            {
              title: 'HLS URL Secure (iOS)',
              property: 'hls_secure_address',
              type: 'hls',
            },
            {
              title: 'HLS Fragmented MP4 URL Secure (iOS)',
              property: 'hls_fmp4_secure_address',
              type: 'hls',
            },
            {
              title: 'HLS Preroll URL Secure',
              property: 'hls_secure_preroll_address',
              type: 'preroll',
            },
            {
              title: 'CLSPS Streaming URL',
              property: 'clsps_address',
              type: 'clsps',
            }
          ).forEach(({
            title,
            property,
            type,
          }) => urlInfo[title] = (<StreamUrlEntry
            streamId={`${streamId}-${property}`}
            urlValue={data[property]}
            type={type} />));
          setDetailListInfo(urlInfo);
        })
        .catch((error) => log.error(`An error occurred while retrieving stream data: ${error}`));
    },
    [streamId]
  );

  // Subscription start
  const emitter = useMessageEmitter();
  useEffectOnce(() => {
    const handler = (event) => {
      const {
        detail: {
          payload: {
            model,
            data,
          },
        },
      } = event ?? {};
      if (!doUpdateFromStreamChange(model, data)) {
        if (!doUpdateFromApplianceChange(model, data, storedApplianceId.current)) {
          return; // do nothing
        }
      }
      getUrlInformation();
    };
    emitter.subscribe(handler);
    return () => emitter.unsubscribe(handler);
  });
  // Subscription end
  useEffect(() => {
    getUrlInformation(); // get the initial information.
  }, [streamId]);

  return (<DetailList keyValuePair={detailListInfo} />);
};